import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  PickupProvider,
  useTillNumber,
  useTenders,
  useBackToTillSummaryEnabled,
  useSubmitError,
  useSubmitSuccess,
  useSubmitLoading,
  useSubmitDisabled,
} from './hooks/PickupProvider';
import PickupDeclarationForm from './PickupDeclarationForm';
import { formatTenderForPickup } from './pickUpRequest';
import { VARIANCE_REASON_ENUM } from './validation';
import { useGetLocalization } from '../../../../hooks';
import { updateItemById } from '../../../../utils/arrayUtils';
import { createTenderData } from '../../../../utils/createTenderData';
import { updateTillPayload } from '../../../../utils/dataUtils';
import { getTills, getTillDetail, tillPickup } from '../../../../utils/service-calls';
import { retrieveClosedTills } from '../../../../utils/tillUtils';
import { ErrorMessage, Subheader, TillSelect, WarningMessage } from '../../../reusable';

const tillStatus = 'PICKED_UP';

function PickupDeclaration() {
  const location = useLocation();
  const { tillNumber, setTillNumber } = useTillNumber();
  const { tenders, setNewTenders } = useTenders();
  const { setBackToTillSummaryEnabled } = useBackToTillSummaryEnabled();
  const { setSubmitError } = useSubmitError();
  const { setSubmitSuccess } = useSubmitSuccess();
  const { setSubmitLoading } = useSubmitLoading();
  const { setSubmitDisabled } = useSubmitDisabled();
  const { tillData: selectedTillFromTillStatusTable } = location.state || {};
  const getLocalization = useGetLocalization();
  const SUBMIT_SUCCESS = getLocalization('msg.till-pickup-declaration-submit-success');

  const [availableTills, setAvailableTills] = useState([]);
  const [availableTillsError, setAvailableTillsError] = useState(null);

  const [retrievedTillError, setRetrievedTillError] = useState(null);
  const [retrievingTills, setRetrievingTills] = useState(true);

  const [selectedTill, setSelectedTill] = useState(null);
  const [currencyCode, setCurrencyCode] = useState('');

  const [loansForTheDay, setLoansForTheDay] = useState({});

  const getSubmitErrorMessage = (e) => {
    return `${getLocalization('msg.failed-to-submit-pickup-declaration')}: ${e.message}`;
  };

  const updateTillStatus = (data) => {
    const updatedPayload = updateTillPayload(data, tillStatus);
    const updatedTills = updateItemById(availableTills, updatedPayload);
    setAvailableTills(retrieveClosedTills(updatedTills));
  };

  useEffect(() => {
    const fetchData = async () =>
      getTills()
        .then(({ data }) => setAvailableTills(retrieveClosedTills(data.tills)))
        .catch((err) => setAvailableTillsError(err))
        .finally(() => setRetrievingTills(false));
    fetchData();
  }, []);

  const retrieveSelectedTill = (tillId, enableBackToTillSummary = false) => {
    setSubmitSuccess('');
    setSelectedTill(null);
    setSubmitLoading(true);
    setSubmitDisabled(false);

    if (enableBackToTillSummary) {
      setBackToTillSummaryEnabled(true);
    }

    return getTillDetail(tillId)
      .then(({ data }) => {
        setCurrencyCode(data.currencyCode);
        setTillNumber(data.tillNumber);
        setLoansForTheDay(data.loansForTheDay);
        const tendersData = data.tenders.map(
          createTenderData({ NO_VARIANCE: VARIANCE_REASON_ENUM.NO_VARIANCE }),
        );
        setNewTenders(tendersData);
        return setSelectedTill(tillId);
      })
      .catch((err) => setRetrievedTillError(err))
      .finally(() => setSubmitLoading(false));
  };

  useEffect(() => {
    if (selectedTillFromTillStatusTable) {
      retrieveSelectedTill(selectedTillFromTillStatusTable.id, !!selectedTillFromTillStatusTable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTillFromTillStatusTable]);

  useEffect(() => {
    setSubmitDisabled(!!(availableTillsError || retrievedTillError));
  }, [availableTillsError, retrievedTillError, setSubmitDisabled]);

  const submit = () => {
    setSubmitError('');
    setSubmitLoading(true);
    setSubmitDisabled(true);
    if (tenders[0].actualAmount < 0) {
      setSubmitLoading(false);
      setSubmitDisabled(false);
      return setSubmitError(
        `${getLocalization('msg.failed-to-submit-pickup-declaration')}: ${getLocalization(
          'msg.till-neg-balance',
        )}`,
      );
    }
    return tillPickup({
      currencyCode,
      loansForTheDay,
      pickUps: tenders.map(formatTenderForPickup),
      tillNumber,
    })
      .then(({ data }) => {
        updateTillStatus(data);
        return setSubmitSuccess(SUBMIT_SUCCESS);
      })
      .catch((e) => {
        setSubmitError(getSubmitErrorMessage(e));
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  return (
    <article className="va-sm-t ta-sm-c ncss-col-sm-12 ncss-col-md-6 ncss-col-lg-4 p4-sm">
      <Subheader>{getLocalization('lbl.till-final-pickup-declaration')}</Subheader>
      {availableTills.length === 0 && (
        <WarningMessage>
          {getLocalization('msg.till-final-pickup-declaration-none-available')}
        </WarningMessage>
      )}
      {availableTillsError ? (
        <ErrorMessage rawError={availableTillsError}>
          {getLocalization('msg.there-was-an-error-loading-tills')}
        </ErrorMessage>
      ) : (
        <TillSelect
          availableTills={availableTills}
          retrieveSelectedTill={retrieveSelectedTill}
          retrievingTills={retrievingTills}
          selectedTill={selectedTill}
        />
      )}
      {retrievedTillError ? (
        <ErrorMessage rawError={retrievedTillError}>
          {getLocalization('msg.there-was-an-error-loading-requested-till')}
        </ErrorMessage>
      ) : (
        selectedTill && <PickupDeclarationForm submit={submit} />
      )}
    </article>
  );
}

export default function PickupDeclarationWithTendersProvider() {
  return (
    <PickupProvider>
      <PickupDeclaration />
    </PickupProvider>
  );
}
